import { Autocomplete, AutocompleteOption, AutocompleteProps } from '@village/ui';
import { useBooking } from 'hooks';
import { FC, useEffect, useId, useMemo } from 'react';
import { Reason } from 'types';
import { getReasonTitle, sortReasons } from 'utils/reason';

export interface ReasonOption extends AutocompleteOption {
    reasonId: number;
}

interface Props extends Omit<AutocompleteProps<ReasonOption, false>, 'onChange' | 'options' | 'value'> {
    label?: string;
    reasons: Reason[] | undefined;
    onChange: (option: ReasonOption | null) => void;
}

const ReasonField: FC<Props> = ({ label, onChange, id, reasons, ...props }) => {
    const localId = useId();
    const { setBookingField, booking } = useBooking();

    const options = useMemo(
        () =>
            sortReasons(reasons ?? []).map<ReasonOption>((reason) => ({
                label: getReasonTitle(reason),
                reasonId: reason.id,
                value: reason.id.toString(),
            })) ?? [],
        [reasons]
    );

    const selectedOption = options.find(({ reasonId }) => booking.reason && reasonId === booking.reason.id);

    useEffect(() => {
        if (!reasons || reasons.length === 0) {
            return;
        }

        // If previously selected reason is no more available (i.e. Department changed)
        if (!selectedOption && booking.reason) {
            return setBookingField({ reason: undefined, previouslySelectedReason: booking.reason });
        }

        // Select the reason that matches the title but different object reference (i.e. Different department)
        const reason = reasons?.find(
            (reasonItem) =>
                booking.reason && reasonItem.name === booking.reason.name && booking.reason.visitType === reasonItem.visitType
        );

        if (selectedOption && (!booking.reason || booking.reason !== reason)) {
            setBookingField({ reason });
        }
    }, [selectedOption, booking.reason, reasons, setBookingField]);

    // TODO: Pass icon after we add support for icons to @village/ui autocomplete
    return (
        <Autocomplete
            aria-label={label}
            blurInputOnSelect={true}
            id={id ?? localId}
            label={label}
            isDisabled={booking.isExistingPatient === undefined || booking.department === undefined}
            isSearchable={false}
            options={options}
            onChange={onChange}
            value={selectedOption ?? null}
            noOptionsMessage={() => 'No reasons available'}
            placeholder="Select reason for appointment"
            menuShouldScrollIntoView={true}
            {...props}
        />
    );
};

export { ReasonField };
